import { getAuthToken, getAuthHeader, API_BASE_URL } from './base';

export const checkResponse = async (surveyId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/checkResponse/${surveyId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return await response.json();
  } catch (error) {
    console.error("Error checking response:", error);
    return { hasResponded: false };
  }
};

export const submitResponse = async (data) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/response/submit-response`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: data,
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error submitting response:", error);
    throw error;
  }
};

export const savePartialResponse = async (surveyId, answers, userId = "anonymous", meta = {}) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/response/save-partial`,
      {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        surveyId,
        answers,
        userId,
        ...meta,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving partial response:", error);
    // Don't throw error for partial saves - just log it
    return null;
  }
};

// Fetches a previously saved partial response so a returning respondent can
// resume where they left off.
export const getPartialResponse = async (surveyId, userId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/response/partial?surveyId=${encodeURIComponent(surveyId)}&userId=${encodeURIComponent(userId)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching partial response:", error);
    return { found: false };
  }
};

export const deleteResponses = async (responseIds) => {
  try {
    // Use getAuthHeader so the active organization (X-Organization-Id) is sent.
    // Without it, the server can't match org-owned surveys and silently deletes
    // nothing while still returning 200 — responses reappear after a refresh.
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const response = await fetch(`${API_BASE_URL}/api/deleteResponses`, {
      method: "DELETE",
      headers,
      body: JSON.stringify({ responseIds }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Delete response error:", {
        status: response.status,
        text: errorText,
      });
      throw new Error(`Failed to delete response ${response.status}`);
    }

    return { success: true };
  } catch (error) {
    console.error("Error in deleteResponses:", error);
    throw error;
  }
};

export const uploadResponseFile = async (file, surveyId) => {
  const token = await getAuthToken();
  const formData = new FormData();
  formData.append("file", file);
  if (surveyId) formData.append("surveyId", surveyId);

  const response = await fetch(`${API_BASE_URL}/api/media/upload-response-file`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || "Upload failed");
  }
  return response.json();
};

export const uploadResponseFilePublic = async (fileOrBlob, surveyId, fileName) => {
  const formData = new FormData();
  if (fileOrBlob instanceof Blob && !(fileOrBlob instanceof File)) {
    formData.append("file", fileOrBlob, fileName || "recording.webm");
  } else {
    formData.append("file", fileOrBlob);
  }
  formData.append("surveyId", surveyId);

  const response = await fetch(`${API_BASE_URL}/api/media/upload-response-file-public`, {
    method: "POST",
    body: formData,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || "Upload failed");
  }
  return response.json();
};
